// Purchasing → Receiving — stripped-down, phone-first list of POs awaiting material. Tap a PO → Receive.
// Gated on `ops.warehouse` so warehouse staff can be given just this tab; PMs (ops.purchasing) see it too.
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import SearchBar from "@/components/common/SearchBar";
import { PillRail, Pill, PillDivider } from "@/components/common/Segmented";
import StickyTop from "@/components/common/StickyTop";
import { POStatusPill, PO_STATUS_LABEL, PO_STATUS_ORDER } from "@/components/purchasing/POStatusPill";
import { ReceivePOModal } from "@/components/purchasing/ReceivePOForm";
import { getProcurementPOs } from "@/lib/api";

// Default (no pill on) = every PO with material still to come. Pills toggle on/off.
const AWAITING = ["approved", "open", "ordered", "acknowledged", "shipped", "partial"];
const VIEWS = [
  { key: "shipped", label: "Shipped", title: "Vendor has shipped — on its way" },
  { key: "partial", label: "Partial", title: "Some lines in, some still open" },
  { key: "received", label: "Received", dividerBefore: true, title: "Fully received" },
];
const inView = (po, view) => {
  if (view === "shipped") return po.status === "shipped";
  if (view === "partial") return po.status === "partial";
  if (view === "received") return po.status === "received" || po.status === "closed";
  return AWAITING.includes(po.status);
};
// ETA as 10/31/26 — short enough for a phone card.
const fmtEta = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return y && m && d ? `${m}/${d}/${String(y).slice(-2)}` : "";
};
const haystack = (po) => [
  po.po_number, po.manufacturer_name, po.wo_number, po.wo_title, po.client_name, po.tracking_number, po.vendor_order_number,
  ...(po.line_items || []).flatMap((li) => [li.description, li.part_number]),
].filter(Boolean).join(" ").toLowerCase();

// Card = PO # + WO name, then who it's from (or ABS Stock · units). Nothing else — the box is in front of you.
function POCard({ po, onOpen }) {
  const units = (po.line_items || []).reduce((s, li) => s + (parseFloat(li.qty) || 0), 0);
  return (
    <button type="button" onClick={onOpen} data-testid={`receiving-po-${po.po_number}`}
      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-left transition-colors hover:border-brand/60 active:bg-slate-50">
      <div className="flex items-center gap-2">
        <span className="shrink-0 font-mono text-sm font-semibold text-brand">{po.po_number}</span>
        {!po.is_stock_po && <span className="truncate text-sm text-slate-800" data-testid={`receiving-po-wo-${po.po_number}`}>{po.wo_title || po.wo_number || ""}</span>}
        {po.status === "partial" && <POStatusPill status="partial" className="ml-auto shrink-0" testId={`receiving-po-status-${po.po_number}`} />}
      </div>
      <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
        <span className="truncate" data-testid={`receiving-po-from-${po.po_number}`}>
          {po.is_stock_po ? <>ABS Stock · <span className="num">{units}</span> unit{units === 1 ? "" : "s"}</> : (po.manufacturer_name || "—")}
        </span>
        {po.eta && <span className="shrink-0 num" data-testid={`receiving-po-eta-${po.po_number}`}>ETA {fmtEta(po.eta)}</span>}
      </div>
    </button>
  );
}

export default function ReceivingTab() {
  const [pos, setPos] = useState(null);
  const [view, setView] = useState("");
  const [search, setSearch] = useState("");
  const [groupBy, setGroupBy] = useState(""); // "" | "status" | "eta" — one grouping at a time
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await getProcurementPOs(); setPos(data.pos || []); }
    catch { toast.error("Could not load POs"); setPos([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (pos || []).filter((po) => (q ? AWAITING.concat("received", "closed").includes(po.status) && haystack(po).includes(q) : inView(po, view)));
  }, [pos, search, view]);
  const searching = search.trim().length > 0;
  // Grouping: Status follows the PO lifecycle; ETA is soonest first with "No ETA" last.
  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const by = new Map();
    const keyOf = (po) => (groupBy === "status" ? (po.status || "") : (po.eta ? String(po.eta).slice(0, 10) : ""));
    rows.forEach((po) => { const k = keyOf(po); if (!by.has(k)) by.set(k, []); by.get(k).push(po); });
    const order = (k) => { const i = PO_STATUS_ORDER.indexOf(k); return i === -1 ? PO_STATUS_ORDER.length : i; };
    const entries = [...by.entries()].sort((a, b) => groupBy === "status"
      ? order(a[0]) - order(b[0])
      : (a[0] === "" ? 1 : b[0] === "" ? -1 : a[0].localeCompare(b[0])));
    return entries.map(([k, list]) => ({
      key: k || "none",
      label: groupBy === "status" ? (PO_STATUS_LABEL[k] || k || "—") : (k ? `ETA ${fmtEta(k)}` : "No ETA"),
      rows: list,
    }));
  }, [rows, groupBy]);
  const toggleGroup = (g) => setGroupBy((cur) => (cur === g ? "" : g));

  return (
    <div className="space-y-3" data-testid="receiving-tab">
      <StickyTop slot="b" testId="receiving-sticky-top">
        <div className="flex flex-wrap items-center gap-2" data-testid="receiving-toolbar">
          <SearchBar value={search} onChange={setSearch} placeholder="PO #, vendor, WO, part…" testId="receiving-search" width="w-full sm:w-72" inputMode="search" />
          <PillRail testId="receiving-view">
            <Pill on={groupBy === "status"} onClick={() => toggleGroup("status")} testId="receiving-group-status" title={groupBy === "status" ? "Stop grouping" : "Group by status"}>Status</Pill>
            <Pill on={groupBy === "eta"} onClick={() => toggleGroup("eta")} testId="receiving-group-eta" title={groupBy === "eta" ? "Stop grouping" : "Group by ETA, soonest first"}>ETA</Pill>
            {VIEWS.map((o) => (
              <Fragment key={o.key}>
                {o.dividerBefore && <PillDivider />}
                <Pill on={!searching && view === o.key} title={view === o.key ? "Back to all open POs" : o.title} testId={`receiving-view-${o.key}`} onClick={() => { setSearch(""); setView((cur) => (cur === o.key ? "" : o.key)); }}>{o.label}</Pill>
              </Fragment>
            ))}
          </PillRail>
          <div className="ml-auto text-xs text-slate-500" data-testid="receiving-count"><span className="num">{rows.length}</span> PO{rows.length === 1 ? "" : "s"}{searching ? " matching" : ""}</div>
        </div>
      </StickyTop>

      {pos === null ? (
        <p className="py-10 text-center text-sm text-slate-400">Loading POs…</p>
      ) : rows.length === 0 ? (
        <p className="py-10 text-center text-sm text-slate-400" data-testid="receiving-empty">
          {searching ? "No POs match." : view === "received" ? "Nothing received yet." : "No purchase orders awaiting material."}
        </p>
      ) : (
        <div className="space-y-2" data-testid="receiving-list">
          {grouped ? grouped.map((g) => (
            <div key={g.key} className="space-y-2" data-testid={`receiving-group-${g.key}`}>
              <div className="px-1 pt-1 text-[11px] font-semibold text-slate-600">{g.label} <span className="font-normal text-slate-400 num">{g.rows.length}</span></div>
              {g.rows.map((po) => <POCard key={po.id} po={po} onOpen={() => setActive(po)} />)}
            </div>
          )) : rows.map((po) => <POCard key={po.id} po={po} onOpen={() => setActive(po)} />)}
        </div>
      )}

      {active && <ReceivePOModal po={active} big onClose={() => setActive(null)} onCheckedIn={() => load()} />}
    </div>
  );
}
